import React, { useState } from 'react';
import { X } from 'lucide-react';
import { colors, typography, radius, spacing } from '../../../theme';
import { parsePhotoType } from '../../../services/checkInService';

const TYPES = ['front', 'side', 'back'];

/**
 * "Starting vs Latest" progress-photo comparison — the primary photo
 * experience, replacing the old per-check-in expanded-card gallery. Attempts
 * matched Front/Side/Back positions via parsePhotoType; any photo whose type
 * can't be parsed renders unlabeled/ordinal rather than a fabricated
 * position. props: { checkIns, onViewHistory }
 */
const ProgressPhotosCompare = ({ checkIns, onViewHistory }) => {
  const [compareOpen, setCompareOpen] = useState(false);
  const list = checkIns || [];

  const starting = list.find(c => c.is_onboarding) || list[0];
  const latestCandidates = list.filter(c => !c.is_onboarding && c.photo_urls?.length > 0);
  const latest = latestCandidates[latestCandidates.length - 1];

  if (!starting?.photo_urls?.length && !latest?.photo_urls?.length) return null;

  const byType = (checkIn) => {
    const map = {};
    const unmatched = [];
    (checkIn?.photo_urls || []).forEach((url) => {
      const type = parsePhotoType(url);
      if (type && !map[type]) map[type] = url;
      else unmatched.push(url);
    });
    return { map, unmatched };
  };

  const startingPhotos = byType(starting);
  const latestPhotos = byType(latest);
  const rowsToShow = TYPES.filter(t => startingPhotos.map[t] || latestPhotos.map[t]);

  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{ ...typography.sectionHeading, color: colors.text, marginBottom: '4px' }}>
        Progress Photos
      </div>
      <div style={{ ...typography.caption, color: colors.textMuted, marginBottom: spacing.md }}>
        Starting vs Latest
      </div>

      {rowsToShow.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, marginBottom: spacing.md }}>
          <div style={{ display: 'flex', gap: spacing.md }}>
            <span style={{ ...typography.label, color: colors.textMuted, flex: 1, textAlign: 'center' }}>STARTING</span>
            <span style={{ ...typography.label, color: colors.textMuted, flex: 1, textAlign: 'center' }}>LATEST</span>
          </div>
          {rowsToShow.map((type) => (
            <div key={type} style={{ display: 'flex', gap: spacing.md, alignItems: 'center' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                {startingPhotos.map[type] ? (
                  <img src={startingPhotos.map[type]} alt={type} style={styles.thumb} />
                ) : (
                  <div style={styles.emptyThumb} />
                )}
              </div>
              <div style={{ ...typography.caption, color: colors.textSubtle, textTransform: 'capitalize', width: '40px', textAlign: 'center' }}>
                {type}
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                {latestPhotos.map[type] ? (
                  <img src={latestPhotos.map[type]} alt={type} style={styles.thumb} />
                ) : (
                  <div style={styles.emptyThumb} />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.md }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.xs, textAlign: 'center' }}>STARTING</div>
            <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap', justifyContent: 'center' }}>
              {(starting?.photo_urls || []).map((url, i) => <img key={i} src={url} alt="" style={styles.thumb} />)}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.xs, textAlign: 'center' }}>LATEST</div>
            <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap', justifyContent: 'center' }}>
              {(latest?.photo_urls || []).map((url, i) => <img key={i} src={url} alt="" style={styles.thumb} />)}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: spacing.md }}>
        <button onClick={() => setCompareOpen(true)} style={styles.primaryBtn}>
          Compare Photos →
        </button>
      </div>
      <button onClick={onViewHistory} style={styles.linkBtn}>
        View check-in history
      </button>

      {compareOpen && (
        <div style={styles.overlay} onClick={() => setCompareOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setCompareOpen(false)} style={styles.closeBtn}>
              <X size={20} color={colors.text} />
            </button>
            <div style={{ display: 'flex', gap: spacing.lg, overflowX: 'auto' }}>
              <div>
                <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.sm, textAlign: 'center' }}>STARTING</div>
                {(starting?.photo_urls || []).map((url, i) => <img key={i} src={url} alt="" style={styles.largePhoto} />)}
              </div>
              <div>
                <div style={{ ...typography.label, color: colors.textMuted, marginBottom: spacing.sm, textAlign: 'center' }}>LATEST</div>
                {(latest?.photo_urls || []).map((url, i) => <img key={i} src={url} alt="" style={styles.largePhoto} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  thumb: {
    width: '80px', height: '100px', objectFit: 'cover', borderRadius: radius.sm,
    border: `1px solid ${colors.border}`,
  },
  emptyThumb: {
    width: '80px', height: '100px', borderRadius: radius.sm,
    border: `1px dashed ${colors.border}`, display: 'inline-block',
  },
  primaryBtn: {
    flex: 1, padding: spacing.md, borderRadius: radius.sm,
    border: `1px solid ${colors.gold}`, background: 'transparent',
    color: colors.gold, fontWeight: '700', fontSize: '14px', cursor: 'pointer',
  },
  linkBtn: {
    display: 'block', width: '100%', minHeight: '44px', marginTop: spacing.sm,
    background: 'none', border: 'none', color: colors.textMuted,
    fontSize: '13px', textDecoration: 'underline', cursor: 'pointer', textAlign: 'center',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: spacing.lg,
  },
  modal: {
    position: 'relative', maxWidth: '90vw', maxHeight: '85vh',
    background: colors.surface1, borderRadius: radius.modal, padding: spacing.lg,
    overflow: 'auto',
  },
  closeBtn: {
    position: 'absolute', top: spacing.sm, right: spacing.sm,
    background: colors.surface2, border: 'none', borderRadius: '50%',
    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', zIndex: 1,
  },
  largePhoto: {
    width: '160px', maxWidth: '38vw', height: 'auto', objectFit: 'cover',
    borderRadius: radius.sm, marginBottom: spacing.sm, display: 'block',
  },
};

export default ProgressPhotosCompare;
